import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import Modal from '../shared/Modal'

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
)

export default function EntriesTab({ selectedAccount }) {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedThread, setSelectedThread] = useState(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [dateRange, setDateRange] = useState('all')

  useEffect(() => {
    loadData()
  }, [selectedAccount])

  const loadData = async () => {
    try {
      setLoading(true)

      let query = supabase
        .from('WhatsAppConversations')
        .select('*')
        .order('Created', { ascending: false })
        .limit(500)

      if (selectedAccount?.WhatsAppAccountId) {
        query = query.eq('WhatsAppAccountId', selectedAccount.WhatsAppAccountId)
      }

      const { data, error } = await query
      if (error) throw error

      setConversations(data || [])
    } catch (error) {
      console.error('Failed to load conversations:', error)
    } finally {
      setLoading(false)
    }
  }

  // Group messages into threads by user phone number
  const getThreads = () => {
    const threadMap = new Map()

    conversations.forEach(msg => {
      // The user number is the non-business number
      // For inbound: From is the user, For outbound: To is the user
      const userNumber = msg.Direction === 'inbound' ? msg.From : msg.To

      if (!threadMap.has(userNumber)) {
        threadMap.set(userNumber, {
          userNumber,
          messages: [],
          lastMessage: null,
          lastTime: null,
          inboundCount: 0,
          outboundCount: 0,
          lastStatus: null
        })
      }

      const thread = threadMap.get(userNumber)
      thread.messages.push(msg)

      if (!thread.lastTime || new Date(msg.Created) > new Date(thread.lastTime)) {
        thread.lastMessage = msg.Body || `[${msg.MessageType}]`
        thread.lastTime = msg.Created
        thread.lastStatus = msg.Status
      }

      if (msg.Direction === 'inbound') thread.inboundCount++
      else thread.outboundCount++
    })

    // Sort messages within each thread chronologically
    threadMap.forEach(thread => {
      thread.messages.sort((a, b) => new Date(a.Created) - new Date(b.Created))
    })

    return Array.from(threadMap.values())
  }

  const getDateRangeStart = (range) => {
    if (range === 'all') return null
    const now = new Date()
    switch (range) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate())
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case 'month':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      default:
        return null
    }
  }

  const threads = getThreads()

  const filteredThreads = threads.filter(thread => {
    const startDate = getDateRangeStart(dateRange)
    if (startDate && new Date(thread.lastTime) < startDate) return false

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      if (
        !thread.userNumber?.toLowerCase().includes(query) &&
        !thread.lastMessage?.toLowerCase().includes(query)
      ) {
        return false
      }
    }

    return true
  })

  // Sort threads by last message time (most recent first)
  filteredThreads.sort((a, b) => new Date(b.lastTime) - new Date(a.lastTime))

  const getStatusBadge = (status) => {
    const statusLower = String(status || '').toLowerCase()
    const badges = {
      delivered: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      read: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
      sent: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
      failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
      undelivered: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
    }
    return badges[statusLower] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }

  const formatPhone = (number) => {
    if (!number) return 'Unknown'
    if (number.length > 15) return 'Business'
    return number.startsWith('+') ? number : `+${number}`
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">WhatsApp Message Log</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {selectedAccount ? `Showing messages for: ${selectedAccount.Name}` : 'Showing all conversations'}
          </p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search phone number or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>

          {(searchQuery || dateRange !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('')
                setDateRange('all')
              }}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        {filteredThreads.length} conversation{filteredThreads.length !== 1 ? 's' : ''} ({conversations.length} total messages)
      </p>

      {/* Conversations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
        {filteredThreads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Messages
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredThreads.map((thread) => (
                  <tr key={thread.userNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer" onClick={() => setSelectedThread(thread)}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatPhone(thread.userNumber)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {thread.lastMessage}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-xs">
                        <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded">
                          {thread.inboundCount} in
                        </span>
                        <span className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200 rounded">
                          {thread.outboundCount} out
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded capitalize ${getStatusBadge(thread.lastStatus)}`}>
                        {thread.lastStatus || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(thread.lastTime)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedThread(thread)
                        }}
                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                      >
                        View Thread
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No conversations found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {conversations.length > 0 ? 'No conversations match your filters' : 'No WhatsApp conversations yet'}
            </p>
          </div>
        )}
      </div>

      {/* Thread Detail Modal */}
      {selectedThread && (
        <Modal title={`Conversation with ${formatPhone(selectedThread.userNumber)}`} onClose={() => setSelectedThread(null)} size="lg">
          <div className="space-y-4">
            {/* Thread Stats */}
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 pb-3 border-b border-gray-200 dark:border-gray-700">
              <span>{selectedThread.messages.length} messages</span>
              <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded text-xs">
                {selectedThread.inboundCount} inbound
              </span>
              <span className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200 rounded text-xs">
                {selectedThread.outboundCount} outbound
              </span>
            </div>

            {/* Chat Thread */}
            <div className="space-y-3 max-h-[500px] overflow-y-auto px-2">
              {selectedThread.messages.map((msg) => {
                const isInbound = msg.Direction === 'inbound'
                return (
                  <div
                    key={msg.ConversationId}
                    className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg px-4 py-2 ${
                        isInbound
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none'
                          : 'bg-green-600 text-white rounded-br-none'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.Body || `[${msg.MessageType}]`}</p>
                      <div className={`flex items-center justify-end space-x-2 mt-1 ${isInbound ? 'text-gray-400 dark:text-gray-500' : 'text-green-200'}`}>
                        <span className="text-[10px]">{formatTime(msg.Created)}</span>
                        {!isInbound && msg.Status && (
                          <span className="text-[10px] capitalize">{msg.Status}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setSelectedThread(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
